import { parseRetryAfter } from '@/utils/common/parseRetryAfter';

/**
 * Shared retry policy (spec §5): GET/HEAD only (enforced by callers),
 * max 2 retries, 429 honors retry-after, 502/503 exponential, 504 never.
 */
export const MAX_RETRIES = 2;

/** A 429 asking to wait longer than this fails fast instead of hanging (spec §5). */
export const RETRY_AFTER_MAX_MS = 60_000;

export const isRetriableStatus = (status: unknown): status is 429 | 502 | 503 =>
  status === 429 || status === 502 || status === 503;

/**
 * Status to drive retry/gateway decisions from an RTK Query error. When the body
 * fails to parse (proxy HTML error pages), fetchBaseQuery reports
 * `status: 'PARSING_ERROR'` with the real HTTP status in `originalStatus` — map
 * it back so an HTML-body 502/503 still retries and toasts (RES-114).
 */
export const effectiveErrorStatus = (error: unknown): number | string | undefined => {
  const e = error as { status?: number | string; originalStatus?: number } | undefined;
  if (e?.status === 'PARSING_ERROR' && typeof e.originalStatus === 'number') {
    return e.originalStatus;
  }
  return e?.status;
};

/**
 * Delay (ms) before the next retry — attempt is 0-based: 0 → 1s, 1 → 2s — or
 * `null` to STOP retrying now. A 429 whose `retry-after` exceeds
 * RETRY_AFTER_MAX_MS is terminal: failing fast beats a silent multi-minute hang
 * (spec §5). 502/503 always use exponential backoff and are never terminal.
 */
export const nextRetryDelayMs = (
  status: number,
  attempt: number,
  retryAfterHeader: string | null | undefined,
): number | null => {
  if (status === 429) {
    const headerMs = parseRetryAfter(retryAfterHeader);
    if (headerMs !== undefined) {
      return headerMs > RETRY_AFTER_MAX_MS ? null : headerMs;
    }
  }
  return 1000 * 2 ** attempt;
};

export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
